import { AxiosError } from "axios";

import { TOAST_TIME_DURATION } from "@/composables/layout/use-toaster";
import { EV4bErrorCode } from "@/data/api/kili/common/kili-errors.type";
import { V4bApiError, V4bClientError, V4bFormFieldApiError } from "@/data/api/v4b/error/v4b-client-error.types";
import i18n from "@/i18n";
import { getV4bErrorCode } from "@/utils/errors/v4b-errors-utils";

import ToastEventBus from "primevue/toasteventbus";

function getFieldErrors(errorDetails: V4bApiError["errorDetails"] | undefined): V4bFormFieldApiError[] | undefined {
  if (!errorDetails || !("fieldErrors" in errorDetails)) {
    return undefined;
  }

  return errorDetails.fieldErrors;
}

function isKiliErrorCode(maybeErrorCode: string | undefined): maybeErrorCode is EV4bErrorCode {
  return maybeErrorCode !== undefined && Object.values(EV4bErrorCode).includes(maybeErrorCode as EV4bErrorCode);
}

function toastErrorCode(maybeErrorCode: string | undefined): boolean {
  if (!isKiliErrorCode(maybeErrorCode)) {
    return false;
  }

  ToastEventBus.emit("add", {
    life: TOAST_TIME_DURATION,
    severity: "error",
    detail: i18n.global.t(`error.${maybeErrorCode}`),
  });

  return true;
}

export function toastKiliError(
  e: unknown,
  fallbackMessage: string = "error.default",
  formPropertyPathTranslationFn?: (propertyPath: string) => string,
) {
  const intl = i18n.global;
  const maybeV4bErrorCode = e instanceof V4bClientError ? getV4bErrorCode(e) : undefined;

  if (toastErrorCode(maybeV4bErrorCode)) {
    return;
  } else if (e instanceof AxiosError) {
    const kiliError = e as AxiosError<V4bApiError>;
    const errorDetails = kiliError.response?.data?.errorDetails;
    const maybeErrorCode = getV4bErrorCode(errorDetails);
    const maybeFieldErrors = getFieldErrors(kiliError);

    if (toastErrorCode(maybeErrorCode)) {
      return;
    } else if (maybeFieldErrors) {
      let formattedFieldErrors: string;

      if (formPropertyPathTranslationFn) {
        formattedFieldErrors = maybeFieldErrors!
          .map((value) => {
            return `${intl.t(formPropertyPathTranslationFn(value.propertyPath!))} [${intl.t(`error.${value.errorCode}`)}]`;
          })
          .join(", ");
      } else {
        formattedFieldErrors = maybeFieldErrors!
          .map((value) => {
            return `${value.propertyPath} [${intl.t(`error.${value.errorCode}`)}]`;
          })
          .join(", ");
      }

      ToastEventBus.emit("add", {
        life: TOAST_TIME_DURATION,
        severity: "error",
        detail: intl.t("error.form", { errors: formattedFieldErrors }),
      });

      return;
    }
  }

  ToastEventBus.emit("add", {
    life: TOAST_TIME_DURATION,
    severity: "error",
    detail: intl.t(fallbackMessage),
  });
}
